import React, { useEffect, useRef, useState } from "react";
import "./CustomDropdown.scss";
import { ReactComponent as ArrowRight } from "../../assets/svg/ArrowRight.svg";
import { ReactComponent as PencilIcon } from "../../assets/svg/pencil.svg";
import { ReactComponent as ArrowDown } from "../../assets/svg/arrowDown.svg";
import { ReactComponent as Cross } from "assets/svg/close.svg";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Row } from "antd";
import ToggleMenu from "../Togglemenu/ToggleMenu";
import { getCustomUnitName } from "../../scenes/ProjectPlanner/components/GoalSettings/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  blukUpdateGoal,
  getPredefinedMetrics,
} from "../../modules/actions/PlanActions";
import { useLocation } from "react-router-dom";

const CustomDropdown = ({
  planDetails,
  handleUpdateGoal,
  handleChange,
  disabled,
}) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const submenuRef = useRef(null);

  const { loading } = useSelector((state) => state.plan.plan);
  const getMetric = useSelector((state) => state?.plan?.predefinedMetrics);
  const isShared = pathname.includes("shared-project");
  const [customItems, setCustomItems] = useState();
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isMenu, setIsMenu] = useState(false);
  const [submenuData, setSubmenuData] = useState(null);
  const [isSubmenuVisible, setIsSubmenuVisible] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState({
    top: -9999,
    left: -9999,
  });
  const [deletedItems, setDeletedItems] = useState([]);
  const [deletedUnits, setDeletedUnits] = useState([]);
  const [errorItems, setErrorItems] = useState([]);
  const [manageCustomUnit, setManageCustomUnit] = useState(false);

  const handleMouseEnter = (event, data, isCustom) => {
    let updatedData = isCustom
      ? data.map((item) => ({ ...item, label: "custom" }))
      : data;

    if (!updatedData || updatedData.length === 0) {
      setIsSubmenuVisible(false);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setSubmenuPosition({
      top: rect.y,
      left: rect.right,
    });

    const sortData = isCustom
      ? [...updatedData].sort((a, b) => a.id - b.id)
      : updatedData;

    setSubmenuData(sortData);
    setIsSubmenuVisible(true);
  };

  const handleMouseLeave = () => {
    setIsSubmenuVisible(false);
  };

  const handleCloseEditModel = () => {
    setManageCustomUnit(false);
    setDeletedItems([]);
    setErrorItems([]);
    setDeletedUnits([]);
  };

  useEffect(() => {
    if (!isShared) {
      dispatch(getPredefinedMetrics());
    }
  }, [dispatch, isShared]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".unit-selected")
      ) {
        setIsMenu(false);
        setIsSubmenuVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddMetric = () => {
    setIsMenu(false);
    setIsSubmenuVisible(false);
    setManageCustomUnit(false);
    handleChange("openCustom", true);
  };

  const capitalizeFirstLetter = (word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  };

  const handleSave = async () => {
    const checkEmptyValue =
      customItems?.filter((item) => item.value === "") || [];
    const checkEmptyItemValue = customItems?.flatMap((item) =>
      item.custom_unit_options.filter((option) => option.value === "")
    );
    const emptyItemIds = [
      ...checkEmptyValue.map((item) => item.id),
      ...checkEmptyItemValue.map((option) => option.id),
    ];
    setErrorItems(emptyItemIds);

    if (checkEmptyValue.length > 0 || checkEmptyItemValue.length > 0) return;

    const matrics = customItems.map((item) => ({
      value: item.value,
      id: item.id,
      goal: item.goal,
    }));

    const units = customItems.flatMap((item) =>
      item.custom_unit_options.map((option) =>
        option.isNew
          ? { value: option.value, label: item.id }
          : { id: option.id, value: option.value, label: item.id }
      )
    );

    const formattedData = {
      labels: [...matrics],
      units: [...units],
      unitsToBeDeleted: deletedUnits || [],
      labelsToBeDeleted: deletedItems || [],
    };

    await dispatch(
      blukUpdateGoal(formattedData, planDetails?.id, handleCloseEditModel)
    );
  };

  const handleUpdateUnit = (unit, label) => {
    setSelectedUnit(unit?.name || unit?.value);
    setIsSubmenuVisible(false);
    setIsMenu(false);
    if (label === "custom") {
      handleUpdateGoal("custom_unit", unit?.id, "custom");
    } else {
      handleUpdateGoal("metric_unit", unit?.id);
    }
  };

  let hideTimeout;

  const handleMouseLeaveParentOrChild = () => {
    hideTimeout = setTimeout(() => {
      setIsSubmenuVisible(false);
      setIsMenu(false);
    }, 400);
  };

  const handleMouseEnterParentOrChild = () => {
    if (disabled) return;

    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }
    setIsMenu(true);
  };

  useEffect(() => {
    const cleanedData = planDetails?.goal?.custom_labels?.map((item) => ({
      ...item,
      custom_unit_options: item.custom_unit_options.filter(
        (option) => option.value !== ""
      ),
    }));

    setCustomItems(cleanedData);
  }, [planDetails.goal?.custom_labels, manageCustomUnit]);

  return (
    <div>
      <Modal
        footer={null}
        closable={false}
        onCancel={handleCloseEditModel}
        title={false}
        open={manageCustomUnit}
        centered
        width={460}
        className="metricModel"
      >
        <div className="metric-model-header">
          <Row justify="space-between" className="mb-4">
            <h3>Manage Custom Metrics</h3>
            <Cross className={"c-pointer"} onClick={handleCloseEditModel} />
          </Row>
        </div>
        <div className="metric-model-body">
          {customItems && customItems?.length > 0 ? (
            customItems?.map((item, index) => {
              const sortData = [...item?.custom_unit_options].sort(
                (a, b) => a.id - b.id
              );
              const data = {
                ...item,
                custom_unit_options: sortData,
              };
              return (
                <ToggleMenu
                  title={item?.value}
                  customItems={customItems}
                  setCustomItems={setCustomItems}
                  deletedItems={deletedItems}
                  setDeletedItems={setDeletedItems}
                  deletedUnits={deletedUnits}
                  setDeletedUnits={setDeletedUnits}
                  errorItems={errorItems}
                  setErrorItems={setErrorItems}
                  planDetails={data}
                  key={index}
                />
              );
            })
          ) : (
            <div className="empty-metric">No Custom Metrics Added</div>
          )}
        </div>
        <div className="metric-model-footer">
          <div
            className="footer-addMetricBtn addMetricBtn"
            onClick={handleAddMetric}
          >
            <PlusOutlined />
            Add Metric
          </div>
          <div className="footer-actions">
            <button onClick={handleCloseEditModel}>Cancel</button>
            <Button onClick={handleSave} disabled={loading} loading={loading}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
      <div
        className={`unit-selected ${isMenu ? "unit-selected-active" : ""}`}
        onClick={() => {
          if (!disabled) {
            setIsMenu((prev) => !prev);
          }
        }}
      >
        <h3>{selectedUnit || getCustomUnitName(planDetails) || "Unit"}</h3>
        <ArrowDown
          style={{
            transform: isMenu ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        />
      </div>

      <div
        ref={dropdownRef}
        className={`customDropdown ${
          isMenu ? "customDropdown-open" : "customDropdown-close"
        }`}
        onMouseLeave={handleMouseLeave}
      >
        <div className="customDropdown-content">
          <div className="customDropdown-box">
            <h3 className="customDropdown-title">Default Metrics</h3>
            <ul className="customDropdown-Menus">
              {getMetric?.map((item, index) => (
                <li
                  key={item?.label}
                  className="customDropdown-Menu"
                  onMouseEnter={(e) => {
                    handleMouseEnter(e, item?.metric_units, false);
                  }}
                >
                  {capitalizeFirstLetter(item?.label)}
                  {item?.metric_units && item?.metric_units?.length > 0 && (
                    <ArrowRight />
                  )}
                </li>
              ))}
            </ul>
          </div>
          {customItems && customItems?.length > 0 && (
            <div className="customDropdown-box">
              <h3 className="customDropdown-title">
                Custom Metrics
                {!disabled && (
                  <PencilIcon
                    className="cursor-pointer"
                    onClick={() => {
                      setManageCustomUnit(true);
                      setIsSubmenuVisible(false);
                      setIsMenu(false);
                    }}
                  />
                )}
              </h3>
              <ul className="customDropdown-Menus">
                {customItems?.map((item, index) => (
                  <li
                    key={item?.value}
                    className="customDropdown-Menu"
                    onMouseEnter={(e) => {
                      handleMouseEnter(e, item?.custom_unit_options, true);
                    }}
                  >
                    {item?.value}
                    {item?.custom_unit_options?.length > 0 && <ArrowRight />}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="addMetricBtn" onClick={handleAddMetric}>
            <PlusOutlined />
            Add Metric
          </div>
          {isSubmenuVisible && submenuData && (
            <div
              ref={submenuRef}
              className={`customDropdown-submenu`}
              style={{ top: submenuPosition.top, left: submenuPosition.left }}
              onMouseEnter={handleMouseEnterParentOrChild}
              onMouseLeave={handleMouseLeaveParentOrChild}
            >
              <ul className="customDropdown-submenu-items">
                {submenuData?.map((item, i) => {
                  const isCustomUnit = item?.label === "custom";

                  return (
                    <li
                      className="customDropdown-submenu-item"
                      key={i}
                      style={{
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                      onClick={() => {
                        if (!disabled) {
                          handleUpdateUnit(
                            item,
                            isCustomUnit ? "custom" : "default"
                          );
                        }
                      }}
                    >
                      {isCustomUnit ? item?.value : item?.name}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomDropdown;
